import MarkdownIt from 'markdown-it';

/**
 * The assistant's reply renderer, shared by the live chat and the super-admin
 * transcript view so the two cannot drift. Replies contain markdown by design:
 * the prompt asks for **bold** labels and numbered steps, and help docs carry
 * navigation links into the product.
 *
 *   const {renderMarkdown} = useAssistantMarkdown(() => clubIdInContext.value);
 *
 * `resolveClubId` supplies the club to substitute into a `{clubId}` placeholder,
 * or null when there is none. Docs write club-scoped paths that way so they need
 * nobody's ids; with no club the link is UNRESOLVABLE and the label is rendered
 * as plain text, because a dead link is worse than no link.
 *
 * Raw HTML is escaped (markdown-it's default), so a reply can never inject markup.
 */
export function useAssistantMarkdown(resolveClubId = () => null) {
  // null when the link cannot be completed.
  const resolveHref = (href) => {
    // markdown-it percent-encodes link destinations while parsing, so a
    // {clubId} placeholder reaches us as %7BclubId%7D. Normalise the braces
    // back before looking for placeholders, or every one of them would slip
    // through as an un-substituted (and broken) link.
    const raw = (href || '').trim().replace(/%7B/gi, '{').replace(/%7D/gi, '}');
    if (!raw) return null;
    let out = raw;
    if (out.includes('{clubId}')) {
      const clubId = resolveClubId();
      if (!clubId) return null;
      out = out.replaceAll('{clubId}', String(clubId));
    }
    if (out.includes('{') || out.includes('}')) return null;  // unknown placeholder
    return out;
  };

  // Links cannot nest in markdown, so this only ever holds one entry, but a
  // stack keeps open/close pairing correct by construction.
  const suppressedLinks = [];
  const md = new MarkdownIt({linkify: true, breaks: true});
  const defaultLinkOpen = md.renderer.rules.link_open ||
      ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));
  md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    const resolved = resolveHref(tokens[idx].attrGet('href'));
    if (resolved === null) {
      // Emit no <a>: the label still renders, so an unfillable placeholder reads
      // as ordinary text. link_close pops this to skip the matching </a>.
      suppressedLinks.push(true);
      return '';
    }
    suppressedLinks.push(false);
    tokens[idx].attrSet('href', resolved);
    if (!resolved.startsWith('/')) {
      tokens[idx].attrSet('target', '_blank');
      tokens[idx].attrSet('rel', 'noopener noreferrer');
    }
    return defaultLinkOpen(tokens, idx, options, env, self);
  };
  const defaultLinkClose = md.renderer.rules.link_close ||
      ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));
  md.renderer.rules.link_close = (tokens, idx, options, env, self) => {
    if (suppressedLinks.pop()) return '';
    return defaultLinkClose(tokens, idx, options, env, self);
  };

  const renderMarkdown = (text) => {
    suppressedLinks.length = 0;  // never carry state between messages
    return md.render(text || '');
  };

  return {renderMarkdown, resolveHref};
}
